import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   Flex,
   ModalCloseButton,
   Button,
   Text,
   RadioGroup,
   Stack,
   Radio,
   UseDisclosureProps,
   Tooltip,
   Code,
   RadioGroupProps,
   useBreakpointValue,
   ModalBody,
} from '@chakra-ui/react';
import { CSSProperties, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import copyToClipboard from 'Shared/copy_to_clipboard';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';

const EMBED_ASPECT_RATIO = 0.73;

export function GuideEmbedModal({
   guideid,
   isOpen,
   onClose,
}: Pick<UseDisclosureProps, 'isOpen' | 'onClose'> & { guideid: number }) {
   const baseUrl = `${window.location.origin}/Guide/embed/${guideid}`;

   const modalWidth = useBreakpointValue(
      { base: 'calc(100% - 32px)', lg: '950px', xl: '1024px' },
      { ssr: false }
   );
   const embedWrapperRef = useRef<HTMLDivElement>();
   const [width, setWidth] = useState('100%');
   const [url, setUrl] = useState(baseUrl);

   const [code, setCode] = useState('');

   useEffect(() => {
      RenderAndSet(<EmbediFrame url={url} width={width} />, setCode);
   }, [url, width]);

   return (
      // @ts-expect-error TS(2322) FIXME: Type 'boolean | undefined' is not assignable to ty... Remove this comment to see the full error message
      <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside">
         <ModalOverlay />
         <ModalContent height="90vh" className="guide-embed-modal" maxWidth={modalWidth}>
            <ModalBody
               display="flex"
               flexDirection={{ base: 'column-reverse', md: 'row' }}
               overflow={{ base: 'auto', md: 'hidden' }}
               padding={0}
            >
               <Flex
                  flexGrow={2}
                  flexShrink={0}
                  width={{ base: '100%', md: '66%' }}
                  maxHeight="100%"
                  justifyContent="center"
                  // @ts-expect-error TS(2322) FIXME: Type 'MutableRefObject<HTMLDivElement | undefined>... Remove this comment to see the full error message
                  ref={embedWrapperRef}
               >
                  <EmbediFrame url={url} width="100%" />
               </Flex>

               <Flex
                  flexGrow={1}
                  flexShrink={0}
                  width={{ base: '100%', md: '33%' }}
                  flexDirection="column"
                  overflow={{ base: 'hidden', md: 'auto' }}
                  borderLeftWidth={1}
               >
                  <Flex flexShrink={0} position="sticky">
                     <ModalHeader flexGrow={1} padding="10px 12px">
                        {_js('Embed this guide')}
                     </ModalHeader>
                     <ModalCloseButton flexShrink={0} />
                  </Flex>
                  <Flex padding="16px 12px" flexDirection="column">
                     <CodeSection code={code} />
                     <RadioSection
                        title={_js('Type')}
                        onChange={setUrl}
                        value={url}
                        values={[
                           { value: baseUrl, label: _js('Single Step') },
                           { value: `${baseUrl}/full`, label: _js('Full Guide') },
                        ]}
                     />
                     <RadioSection
                        title={_js('Width')}
                        onChange={setWidth}
                        value={width}
                        values={[
                           { value: '100%', label: _js('Full (100%)') },
                           { value: '1200px', label: _js('Large (1200px)') },
                           { value: '800px', label: _js('Medium (800px)') },
                           { value: '400px', label: _js('Small (400px)') },
                        ]}
                     />
                  </Flex>
               </Flex>
            </ModalBody>
         </ModalContent>
      </Modal>
   );
}

function RenderAndSet(element: JSX.Element, setString: Dispatch<SetStateAction<string>>) {
   const div = document.createElement('div');
   const root = createRoot(div);

   queueMicrotask(() => {
      flushSync(() => {
         root.render(element);
      });

      const string = div.innerHTML;
      div.remove();
      setString(string);
   });
}

function EmbediFrame({ url, width, style }: { url: string; width: string; style?: CSSProperties }) {
   const aspectHeight = width === '100%' ? '100%' : Number.parseInt(width) * EMBED_ASPECT_RATIO;
   return (
      <iframe
         src={url}
         allowFullScreen
         style={{ border: 0, width, height: aspectHeight, maxHeight: '100%', ...style }}
      />
   );
}

function CodeSection({ code }: { code: string }) {
   const [isOpen, setIsOpen] = useState(false);

   const onCopyClick = () => {
      setIsOpen(true);
      copyToClipboard(code);
      setTimeout(() => {
         setIsOpen(false);
      }, 1000);
   };

   return (
      <>
         <Code
            background="gray.200"
            borderRadius="4px"
            padding="12px"
            fontSize="14px"
            lineHeight="18px"
            color="gray.700"
            marginBottom="8px"
         >
            {code}
         </Code>
         <Tooltip hasArrow label={_js('Copied!')} isOpen={isOpen}>
            <Button onClick={onCopyClick} variant="solid" colorScheme="blue" marginBottom="16px">
               {_js('Copy embed code')}
            </Button>
         </Tooltip>
      </>
   );
}

function RadioSection({
   onChange,
   value,
   values,
   title,
}: {
   onChange: RadioGroupProps['onChange'];
   value: RadioGroupProps['value'];
   values: { value: string; label: string }[];
   title: string;
}) {
   const radios = values.map(({ value, label }) => (
      <Radio key={value} value={value}>
         {label}
      </Radio>
   ));

   return (
      <RadioGroup onChange={onChange} value={value}>
         <Stack direction="column" marginTop="16px" borderTopWidth={1} spacing="12px">
            <Text
               fontWeight="medium"
               fontSize="14px"
               lineHeight="17px"
               color="gray.900"
               marginTop="16px"
               marginBottom="8px"
            >
               {title}
            </Text>
            {radios}
         </Stack>
      </RadioGroup>
   );
}
